import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminShell } from './AdminShell';
import { adminApi, AdminAiSpend } from '@/lib/adminApi';

export function AdminAiSpendPage() {
  const { t } = useTranslation();
  const [sinceDays, setSinceDays] = useState(30);

  const list = useQuery<AdminAiSpend[]>({
    queryKey: ['admin', 'ai-spend', sinceDays],
    queryFn: () => adminApi.aiSpend(sinceDays),
  });

  const rows = list.data ?? [];
  const totalCents = rows.reduce((s, r) => s + (r.total_cost_cents || 0), 0);
  const totalRuns = rows.reduce((s, r) => s + (r.run_count || 0), 0);

  return (
    <AdminShell titleKey="admin.ai_spend.title">
      <div className="mb-4 flex items-center gap-3">
        <label className="text-xs text-ink-600 flex items-center gap-2">
          {t('admin.ai_spend.since_days')}
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
            className="rounded-md border border-ink-300 px-2 py-1 text-sm"
          >
            <option value={7}>7</option>
            <option value={30}>30</option>
            <option value={90}>90</option>
            <option value={180}>180</option>
            <option value={365}>365</option>
          </select>
        </label>
        <span className="text-xs text-ink-500">
          {t('admin.ai_spend.total_runs')}: {totalRuns.toLocaleString()}
        </span>
        <span className="text-xs text-ink-500">
          {t('admin.ai_spend.total_spend')}: ¥{Math.round(totalCents / 100).toLocaleString()}
        </span>
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {!list.isLoading && rows.length === 0 && (
        <p className="text-sm text-ink-500">{t('admin.ai_spend.empty')}</p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_spend.col_workspace')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_spend.col_company')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_spend.col_runs')}</th>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.ai_spend.col_total_cost')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.workspace_id} className="border-b border-ink-100">
                  <td className="py-2.5 px-3 font-medium text-ink-900">{r.workspace_name}</td>
                  <td className="py-2.5 px-3 text-ink-700">{r.company_name}</td>
                  <td className="py-2.5 px-3 num text-ink-700">
                    {r.run_count.toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-900 font-medium">
                    ¥{Math.round(r.total_cost_cents / 100).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
